import { STORES, STORE_KEYS, LABELS, DYNAMIC_LABELS } from '@constants'
import { saveToStorage } from '@helpers/storage.helpers'
import { Store } from '@stores'
import {
  ICompanyInvoice,
  ICompanyInvoiceClient,
  ICompanyInvoiceTask,
  ISingleDocument,
  ISingleDocumentLine,
  PaymentStatus,
} from '@typings'
import { saveAs } from 'file-saver'
import { action, autorun, makeAutoObservable, runInAction } from 'mobx'
import { FieldValues } from 'react-hook-form'
import { DatagridStore } from './datagrid.stores'
import { getFilenameFromContentDisposition } from '@helpers/getFilenameFromContentDisposition'

const defaultValues: Record<string, any> = {
  list: [],
  single: {} as ISingleDocument,
  concept_number: null as string | null,
}
export class InvoiceStore {
  [key: string]: any
  loading: boolean
  list: any[]
  data_grid: DatagridStore
  single: ISingleDocument
  concept_number: string | null
  store: Store

  constructor(store: Store) {
    this.store = store
    this.loading = true
    this.data_grid = new DatagridStore(this.store, STORES.INVOICE as string, {
      type: 'invoice',
    })
    this.list = defaultValues.list
    this.single = defaultValues.single
    this.concept_number = defaultValues.concept_number
    makeAutoObservable(this)
  }

  @action
  getList = async (query?: any) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const params = {
        type: 'invoice',
        ...query,
      }
      const res = await this.store.api.documents.getAll({ params })
      this.set(STORE_KEYS.LIST, res.data)
    } catch (e) {
      console.log(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getSingle = async (id: ISingleDocument['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.documents.getSingle(id)
      this.set(STORE_KEYS.SINGLE, result)
      return Promise.resolve(result)
    } catch (e) {
      console.error(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  downloadPdf = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.downloadPdf(this.single.id)
      const fileName = getFilenameFromContentDisposition(res.headers['content-disposition'])
      if (fileName) {
        saveAs(res.data, fileName)
      }
    } catch (e: any) {
      let errorMessage = 'Er is iets misgegaan bij het downloaden van de PDF'
      try {
        // Error response is a blob, need to convert to text first
        if (e instanceof Blob) {
          const text = await e.text()
          const errorData = JSON.parse(text)
          errorMessage = errorData.message || errorMessage
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message
        }
      } catch {
        // If parsing fails, use default message
      }
      this.store.ui.handleErrorToast(errorMessage)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  previewPdf = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.previewPdf(this.single.id)
      return Promise.resolve(res.data)
    } catch (e: any) {
      let errorMessage = 'Er is een fout opgetreden bij het laden van de voorvertoning'
      try {
        if (e instanceof Blob) {
          const text = await e.text()
          const errorData = JSON.parse(text)
          errorMessage = errorData.error || errorData.message || errorMessage
        } else if (e?.response?.data?.error) {
          errorMessage = e.response.data.error
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message
        }
      } catch {
        // If parsing fails, use default message
      }
      this.store.ui.handleErrorToast(errorMessage)
      return Promise.reject(new Error(errorMessage))
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  downloadUbl = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.downloadUbl(this.single.id)
      const fileName = getFilenameFromContentDisposition(res.headers['content-disposition'])
      if (fileName) {
        saveAs(res.data, fileName)
      }
    } catch (e: any) {
      let errorMessage = 'Er is iets misgegaan bij het downloaden van het UBL-bestand'
      try {
        if (e instanceof Blob) {
          const text = await e.text()
          const errorData = JSON.parse(text)
          errorMessage = errorData.message || errorMessage
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message
        }
      } catch {
        // If parsing fails, use default message
      }
      this.store.ui.handleErrorToast(errorMessage)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  downloadBulkPdf = async (documentIds: number[]) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.downloadBulkPdf(documentIds)
      const fileName = getFilenameFromContentDisposition(res.headers['content-disposition']) || 'facturen.zip'
      if (fileName) {
        saveAs(res.data, fileName)
      }
    } catch (e: any) {
      let errorMessage = 'Er is iets misgegaan bij het downloaden van de PDF'
      try {
        // Error response is a blob, need to convert to text first
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text()
          const errorData = JSON.parse(text)
          errorMessage = errorData.message || errorMessage
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message
        }
      } catch {
        // If parsing fails, use default message
      }
      this.store.ui.handleErrorToast(errorMessage)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  downloadBulkZip = async (documentIds: number[]) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.downloadBulkZip(documentIds)
      const fileName =
        getFilenameFromContentDisposition(res.headers['content-disposition']) ||
        `facturen-export-${new Date().toISOString().split('T')[0]}.zip`
      if (fileName) {
        saveAs(res.data, fileName)
      }
    } catch (e: any) {
      let errorMessage = 'Er is iets misgegaan bij het downloaden van de facturen'
      try {
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text()
          const errorData = JSON.parse(text)
          errorMessage = errorData.message || errorMessage
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message
        }
      } catch {
        // If parsing fails, use default message
      }
      this.store.ui.handleErrorToast(errorMessage)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  finalise = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.ui.confirm({
        title: 'Factuur definitief maken?',
        content:
          // eslint-disable-next-line max-len
          'Door akkoord te gaan maak je de factuur definitief. Je kan deze hierna niet meer wijzigen. Indien je een definitieve factuur ongedaan wil maken, dien je een credit nota te maken. Weet je zeker dat je de factuur definitief wilt maken?',
        withCancel: true,
      })
      const res = await this.store.api.documents.finalise(this.single.id, this.single)
      this.set(STORE_KEYS.SINGLE, res)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  update = async (data: FieldValues) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.update(this.single.id, data as ISingleDocument)
      this.set(STORE_KEYS.SINGLE, res)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  create = async (data: FieldValues) => {
    this.set(STORE_KEYS.LOADING, true)

    try {
      if (!this.store.company.current_company_id) {
        await this.store.company.getMyCompany()
      }
      const _data: Partial<ISingleDocument> = {
        ...data,
        type: 'invoice',
        company_id: this.store.company.current_company_id,
      }
      const res = await this.store.api.documents.create(_data)
      this.set(STORE_KEYS.SINGLE, res)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  updatePaymentStatus = async (id: ISingleDocument['id'], paymentStatus: PaymentStatus) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      // If changing to paid status, confirm and create payment
      if (paymentStatus === 'paid') {
        await this.store.ui.confirm({
          title: 'Factuur als betaald markeren?',
          content:
            'Door akkoord te gaan wordt de factuur als betaald gemarkeerd en wordt automatisch een ' +
            'betaling aangemaakt voor het volledige openstaande bedrag. Weet je zeker dat je de ' +
            'factuur als betaald wilt markeren?',
          withCancel: true,
        })

        // Create payment for the full outstanding amount
        const outstandingAmount = this.single.outstanding_amount || this.single.total
        if (outstandingAmount > 0) {
          await this.store.api.payment.createPayment(id, {
            amount: outstandingAmount,
            payment_date: new Date().toISOString().split('T')[0],
            payment_method: 'bank_transfer',
          })
        }
      }

      const res = await this.store.api.documents.updateStatus(id, { payment_status: paymentStatus })
      this.set(STORE_KEYS.SINGLE, res)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  createCreditNote = async (id: ISingleDocument['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.ui.confirm({
        title: 'Creditnota aanmaken?',
        content:
          'Door akkoord te gaan wordt er een creditnota aangemaakt op basis van deze factuur. ' +
          'De creditnota wordt als concept aangemaakt zodat je deze eventueel nog kunt aanpassen. ' +
          'De originele factuur krijgt de status "Gecrediteerd". Wil je doorgaan?',
        withCancel: true,
      })

      const res = await this.store.api.documents.createCreditNote(id)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  duplicate = async (id: ISingleDocument['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.ui.confirm({
        title: LABELS.CONFIRM_DUPLICATE_INVOICE_TITLE,
        content: LABELS.CONFIRM_DUPLICATE_INVOICE_TEXT,
        withCancel: true,
      })

      const res = await this.store.api.documents.duplicate(id)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  delete = async (id: ISingleDocument['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.ui.confirm({
        title: 'Factuur verwijderen?',
        content:
          'Weet je zeker dat je deze conceptfactuur wilt verwijderen? ' +
          'Deze actie kan niet ongedaan worden gemaakt.',
        withCancel: true,
      })

      await this.store.api.documents.delete(id)
      return Promise.resolve()
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  deleteBulk = async (documentIds: number[]) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const count = documentIds.length

      await this.store.ui.confirm({
        title: DYNAMIC_LABELS.CONFIRM_BULK_DELETE_INVOICES_TITLE(count),
        content: DYNAMIC_LABELS.CONFIRM_BULK_DELETE_INVOICES_TEXT(count),
        withCancel: true,
      })

      await this.store.api.documents.deleteBulk(documentIds)
      return Promise.resolve()
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getConceptNumber = async (id: ISingleDocument['id']) => {
    try {
      const res = await this.store.api.documents.getConceptNumber(id)
      this.set('concept_number', res.concept_number)
      return Promise.resolve(res.concept_number)
    } catch {
      this.set('concept_number', null)
      return Promise.resolve(null)
    }
  }

  @action
  reset = (withLoading?: boolean) => {
    runInAction(() =>
      Object.keys(defaultValues).map(key => {
        this.set(key, defaultValues[key])
      })
    )
    if (withLoading) this.set(STORE_KEYS.LOADING, false)
  }

  @action
  set = (target: string, value: unknown, save?: boolean): unknown => {
    if (!target) return
    this[target] = value
    if (save) saveToStorage(target, value)
  }
}
